import React from 'react';
import styled from 'styled-components';
import { AddPersonTile } from './AddPersonTile';

/**
 * The signed-in "Your people" region when it is NOT showing a grid of people.
 * One honest state for both a first-time account and a returning-but-empty one
 * (we can't reliably tell them apart — spec §2), plus the two "not actually
 * empty" states the region must never render as empty:
 *
 *   - `empty`   — confirmed zero boards → a neutral lead + the add-person tile.
 *                 The lead asserts neither "you're new" nor "your boards are
 *                 gone", so it's honest for a newcomer AND a returning user.
 *   - `error`   — the board fetch failed → a retry, never "no boards".
 *   - `timeout` — the fetch is still pending past the budget → a retry.
 *
 * `error`/`timeout` are announced (`role="status"`); the three variants carry
 * distinct accessible text so a screen reader hears which state it is
 * (spec §11).
 */
export type BoardsEmptyVariant = 'empty' | 'error' | 'timeout';

export interface BoardsEmptyStateProps {
  variant: BoardsEmptyVariant;
  /** `empty` — enter the add-a-person flow. */
  onAddPerson?: () => void;
  /** `error`/`timeout` — re-subscribe the listener. */
  onRetry?: () => void;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 40px 16px 8px;
`;

const Lead = styled.p`
  margin: 0;
  max-width: 32ch;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.4;
  color: hsl(var(--foreground));
`;

const Muted = styled.p`
  margin: 0;
  max-width: 32ch;
  font-size: 15px;
  line-height: 1.4;
  color: hsl(var(--muted-foreground));
`;

// Constrain the add-person tile so the dashed "+" card reads as a single
// tile (its own aspect-ratio is 4/5 at 100% width) rather than stretching
// the full section width.
const TileHolder = styled.div`
  width: 100%;
  max-width: 200px;
`;

const RetryButton = styled.button`
  appearance: none;
  border: 1.5px solid hsl(var(--border));
  background: transparent;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 15px;
  font-weight: 600;
  color: hsl(var(--foreground));
  cursor: pointer;
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px hsl(var(--primary));
  }
`;

export const BOARDS_EMPTY_LEAD = 'This is where your people show up.';
const ADD_PERSON_LABEL = 'Add someone you’re shopping for';
const ERROR_MESSAGE = 'Couldn’t load your boards.';
const TIMEOUT_MESSAGE = 'Still loading your boards…';

export const BoardsEmptyState: React.FC<BoardsEmptyStateProps> = ({
  variant,
  onAddPerson,
  onRetry,
}) => {
  if (variant === 'empty') {
    return (
      <Wrap data-testid="boards-empty">
        <Lead>{BOARDS_EMPTY_LEAD}</Lead>
        <TileHolder>
          <AddPersonTile label={ADD_PERSON_LABEL} onClick={onAddPerson} />
        </TileHolder>
      </Wrap>
    );
  }

  const message = variant === 'error' ? ERROR_MESSAGE : TIMEOUT_MESSAGE;
  return (
    <Wrap
      role="status"
      data-testid={variant === 'error' ? 'boards-error' : 'boards-timeout'}
    >
      <Muted>{message}</Muted>
      <RetryButton type="button" onClick={onRetry}>
        Try again
      </RetryButton>
    </Wrap>
  );
};
